import type { QueryClient } from "@tanstack/react-query";

const PREFIXES_STOCKAGE_SESSION = [
  "champ-erp:recherche-recents",
  "champ-erp:pressurage:marc-brouillon",
] as const;
const CACHES_RESEAU_LEGACY = ["trpc-api-cache"] as const;

/** Supprime les métadonnées d'interface persistées par une session ERP. */
function purgerStockageLegerSession(): void {
  try {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (key && PREFIXES_STOCKAGE_SESSION.some((prefixe) => key.startsWith(prefixe))) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Stockage indisponible (mode privé strict) : aucune donnée lisible à purger.
  }
}

/** Retire les anciennes réponses tRPC Workbox, supprimées de la config PWA. */
async function purgerCachesReseauLegacy(): Promise<void> {
  try {
    if (typeof caches === "undefined") return;
    await Promise.all(CACHES_RESEAU_LEGACY.map((cacheName) => caches.delete(cacheName)));
  } catch {
    // Le service worker ne consultera plus ces caches ; la suppression reste
    // best-effort sur les navigateurs qui bloquent l'API Cache Storage.
  }
}

/**
 * Purge les données locales liées à l'identité avant une sortie de session.
 *
 * L'ordre est volontaire :
 * 1. annuler les lectures encore en vol ;
 * 2. vider tous les caches React Query (pas seulement `auth.me`) ;
 * 3. retirer les métadonnées localStorage liées à la session ;
 * 4. vider tous les stores Dexie et l'ancien cache Workbox tRPC.
 *
 * La purge Dexie reste chargée dynamiquement afin de ne pas ajouter Dexie au
 * bundle initial des écrans publics.
 */
export async function purgerContexteSessionLocal(
  queryClient: QueryClient,
  purgerStockageHorsLigne: () => Promise<void> = async () => {
    const offline = await import("../offline/index.js");
    await offline.viderStockageHorsLigne();
  },
): Promise<void> {
  await queryClient.cancelQueries();
  queryClient.clear();
  purgerStockageLegerSession();
  await Promise.all([purgerStockageHorsLigne(), purgerCachesReseauLegacy()]);
}
